import React, { useState, useEffect, useMemo, useRef } from "react";
import { useResources } from "../../hooks/useResources";
import { useCountriesStore } from "../../stores/countries.store";
import { useSubjectsStore } from "../../stores/subjects.store";
import { useLanguagesStore } from "../../stores/languages.store";
import { useGradesStore } from "../../stores/grades.store";
import { useSectionsStore } from "../../stores/sections.store";
import CustomFormModal from "./CustomFormModal";
import CustomSelectDropdown from "./CustomSelectDropdown";
import type { SelectOption } from "../../interfaces/componentInterfaces/dropdown";
import type { Section } from "../../interfaces/apiInterfaces/sections";
import "./AddSectionModal.scss";

export interface EditSectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  section: Section;
}

const EditSectionModal: React.FC<EditSectionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  section,
}) => {
  const { getResourceValue } = useResources();
  const { countries, load: loadCountries } = useCountriesStore();
  const { subjects, allSubjects, load: loadSubjects, filterByGradeAndLanguage, getAvailableLanguagesForGrade } = useSubjectsStore();
  const { languages, load: loadLanguages } = useLanguagesStore();
  const { grades, load: loadGrades, filterByCountry } = useGradesStore();
  const { updateSection } = useSectionsStore();

  const [formData, setFormData] = useState({
    title: "",
    country: "",
    subject: "",
    language: "",
    grade: "",
    term: "",
  });

  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Refs to track previous values and prevent reset during initialization
  const prevCountryRef = useRef<string>("");
  const prevGradeRef = useRef<string>("");
  const prevLanguageRef = useRef<string>("");

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen) {
      loadCountries();
      loadLanguages();
      loadGrades();
      setError(null);
      setIsInitialized(false);
      // Reset refs when modal opens
      prevCountryRef.current = "";
      prevGradeRef.current = "";
      prevLanguageRef.current = "";
    }
  }, [isOpen, loadCountries, loadLanguages, loadGrades]);

  // Handle edit mode initialization
  useEffect(() => {
    const initializeEditMode = async () => {
      if (isOpen && section && grades.length > 0 && !isInitialized) {
        const grade = grades.find(
          (g) => g.grade_localization_key === section.grade_localization_key
        );
        const countryIsoCode = grade?.country_iso_code?.toLowerCase() || "";
        const languageCode = section.subject_language_iso_code.toLowerCase();

        // Load subjects first if grade exists
        if (grade?.grade_id) {
          await loadSubjects(grade.grade_id);
          // Filter by both grade and language
          filterByGradeAndLanguage(grade.grade_id, languageCode);
        }

        // Set form data after subjects are loaded
        setFormData({
          title: section.section_title,
          country: countryIsoCode,
          subject: section.subject_id,
          language: languageCode,
          grade: grade?.grade_id || "",
          term: section.term_code,
        });

        // Update refs to match initialized values (prevents reset on first render)
        prevCountryRef.current = countryIsoCode;
        prevGradeRef.current = grade?.grade_id || "";
        prevLanguageRef.current = languageCode;

        setIsInitialized(true);
      }
    };

    initializeEditMode();
  }, [isOpen, section, grades, loadSubjects, filterByGradeAndLanguage, isInitialized]);

  useEffect(() => {
    if (formData.country) {
      filterByCountry(formData.country);
    } else {
      filterByCountry(null);
    }
  }, [formData.country, filterByCountry]);

  // Filter grades to Egypt (country_iso_code = 'eg') when grades are loaded and modal is open
  useEffect(() => {
    if (isOpen && grades.length > 0) {
      //filterByCountry('eg');
    }
  }, [isOpen, grades.length, filterByCountry]);

  useEffect(() => {
    const loadAndFilterSubjects = async () => {
      if (formData.grade && formData.language) {
        // Load subjects first, then filter
        await loadSubjects(formData.grade);
        filterByGradeAndLanguage(formData.grade, formData.language);
      } else if (formData.grade) {
        // Load subjects for the grade
        await loadSubjects(formData.grade);
        filterByGradeAndLanguage(formData.grade, null);
      } else {
        // No grade selected, load all subjects
        await loadSubjects();
        filterByGradeAndLanguage(null, null);
      }
    };

    loadAndFilterSubjects();
  }, [formData.grade, formData.language, loadSubjects, filterByGradeAndLanguage]);

  // Reset dependent fields when country changes (only after initialization and actual change)
  useEffect(() => {
    if (isInitialized && formData.country !== prevCountryRef.current) {
      prevCountryRef.current = formData.country;
      setFormData((prev) => ({ ...prev, grade: "", language: "", subject: "", term: "" }));
      // Also reset the dependent refs
      prevGradeRef.current = "";
      prevLanguageRef.current = "";
    }
  }, [formData.country, isInitialized]);

  // Reset dependent fields when grade changes (only after initialization and actual change)
  useEffect(() => {
    if (isInitialized && formData.grade !== prevGradeRef.current) {
      prevGradeRef.current = formData.grade;
      setFormData((prev) => ({ ...prev, language: "", subject: "", term: "" }));
      // Also reset the dependent ref
      prevLanguageRef.current = "";
    }
  }, [formData.grade, isInitialized]);

  // Reset subject when language changes (only after initialization and actual change)
  useEffect(() => {
    if (isInitialized && formData.language !== prevLanguageRef.current) {
      prevLanguageRef.current = formData.language;
      setFormData((prev) => ({ ...prev, subject: "", term: "" }));
    }
  }, [formData.language, isInitialized]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSelectChange = (name: string) => (value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.title.trim()) {
      setError(getResourceValue("title_required", "Title is required"));
      return;
    }

    if (!formData.subject) {
      setError(getResourceValue("subject_required", "Please select a subject"));
      return;
    }

    if (!formData.language) {
      setError(getResourceValue("language_required", "Please select a language"));
      return;
    }

    if (!formData.grade) {
      setError(getResourceValue("grade_required", "Please select a grade"));
      return;
    }

    if (!formData.term) {
      setError(getResourceValue("term_required", "Please select a term"));
      return;
    }

    setIsLoading(true);
    try {
      await updateSection(section.section_id, {
        subject_id: formData.subject,
        term_code: formData.term,
        section_title: formData.title.trim(),
        section_is_original: true,
        section_original_section_id: null,
        section_language_iso_code: formData.language.toUpperCase(),
      });

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err: any) {
      setError(err?.message || getResourceValue("update_section_failed", "Failed to update section"));
    } finally {
      setIsLoading(false);
    }
  };

  const countryOptions: SelectOption[] = [
    { value: "", label: getResourceValue("select_country", "Select Country") },
    ...(countries || []).map((country) => ({
      value: country.country_iso_code.toLowerCase(),
      label: country.country_english_name,
    })),
  ];

  // Filter grade options based on selected country
  const gradeOptions: SelectOption[] = useMemo(() => {
    const baseOptions: SelectOption[] = [
      { value: "", label: getResourceValue("select_grade", "Select Grade") },
    ];

    // If no country is selected, only show the placeholder
    if (!formData.country) {
      return baseOptions;
    }

    // Filter grades by selected country (case-insensitive comparison)
    const filteredGrades = (grades || []).filter(
      (grade) => grade.country_iso_code.toLowerCase() === formData.country.toLowerCase()
    );

    return [
      ...baseOptions,
      ...filteredGrades.map((grade) => ({
        value: grade.grade_id,
        label: getResourceValue(grade.grade_localization_key, grade.grade_title),
      })),
    ];
  }, [formData.country, grades, getResourceValue]);

  // Filter language options based on selected grade
  const languageOptions: SelectOption[] = useMemo(() => {
    const baseOptions: SelectOption[] = [
      { value: "", label: getResourceValue("select_language", "Select Language") },
    ];

    // If no grade is selected, only show the placeholder
    if (!formData.grade) {
      return baseOptions;
    }

    // If languages haven't loaded yet, only show placeholder
    if (!languages || languages.length === 0) {
      return baseOptions;
    }

    // If subjects haven't loaded yet, only show placeholder
    if (!allSubjects || allSubjects.length === 0) {
      return baseOptions;
    }

    // Get available languages for the selected grade
    const availableLanguageCodes = getAvailableLanguagesForGrade(formData.grade);

    // If no languages available for this grade, show placeholder only
    if (availableLanguageCodes.length === 0) {
      return baseOptions;
    }

    const availableLanguageCodesLower = availableLanguageCodes.map((code) => code.toLowerCase());

    // Filter languages to only include those available for the selected grade
    const filteredLanguages = languages.filter((language) =>
      availableLanguageCodesLower.includes(language.language_iso_code.toLowerCase())
    );

    return [
      ...baseOptions,
      ...filteredLanguages.map((language) => ({
        value: language.language_iso_code.toLowerCase(),
        label: language.language_english_name,
      })),
    ];
  }, [formData.grade, languages, allSubjects, getAvailableLanguagesForGrade, getResourceValue]);

  // Filter subject options based on selected grade and language
  const subjectOptions: SelectOption[] = useMemo(() => {
    const baseOptions: SelectOption[] = [
      { value: "", label: getResourceValue("select_subject", "Select Subject") },
    ];

    // If no grade is selected, only show the placeholder
    if (!formData.grade) {
      return baseOptions;
    }

    // If no language is selected, only show the placeholder
    if (!formData.language) {
      return baseOptions;
    }

    // Show subjects filtered by grade and language
    return [
      ...baseOptions,
      ...(subjects || []).map((subject) => ({
        value: subject.subject_id,
        label: subject.subject_title,
      })),
    ];
  }, [formData.grade, formData.language, subjects, getResourceValue]);

  const termOptions: SelectOption[] = [
    { value: "", label: getResourceValue("select_term", "Select Term") },
    { value: "t0", label: getResourceValue("term_0", "Term 0") },
    { value: "t1", label: getResourceValue("term_1", "Term 1") },
    { value: "t2", label: getResourceValue("term_2", "Term 2") },
  ];

  // Check if all required fields are filled
  const isFormValid = useMemo(() => {
    return (
      formData.title.trim() !== "" &&
      formData.grade !== "" &&
      formData.language !== "" &&
      formData.subject !== "" &&
      formData.term !== ""
    );
  }, [formData.title, formData.grade, formData.language, formData.subject, formData.term]);

  return (
    <CustomFormModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={getResourceValue("edit_section", "Edit Section")}
      submitButtonText={getResourceValue("save", "Save")}
      isLoading={isLoading}
      error={error}
      isSubmitDisabled={!isFormValid}
    >
      <div className="add-section-modal__field">
        <input
          type="text"
          name="title"
          placeholder={getResourceValue("title", "Title")}
          value={formData.title}
          onChange={handleInputChange}
          autoComplete="off"
          className="add-section-modal__input"
        />
      </div>

      <div className="add-section-modal__field">
        <CustomSelectDropdown
          id="country-select"
          options={countryOptions}
          value={formData.country}
          onChange={handleSelectChange("country")}
          placeholder={getResourceValue("select_country", "Select Country")}
        />
      </div>

      <div className="add-section-modal__field">
        <CustomSelectDropdown
          id="grade-select"
          options={gradeOptions}
          value={formData.grade}
          onChange={handleSelectChange("grade")}
          placeholder={getResourceValue("select_grade", "Select Grade")}
        />
      </div>

      <div className="add-section-modal__field">
        <CustomSelectDropdown
          id="language-select"
          options={languageOptions}
          value={formData.language}
          onChange={handleSelectChange("language")}
          placeholder={getResourceValue("select_language", "Select Language")}
        />
      </div>

      <div className="add-section-modal__field">
        <CustomSelectDropdown
          id="subject-select"
          options={subjectOptions}
          value={formData.subject}
          onChange={handleSelectChange("subject")}
          placeholder={getResourceValue("select_subject", "Select Subject")}
        />
      </div>

      <div className="add-section-modal__field">
        <CustomSelectDropdown
          id="term-select"
          options={termOptions}
          value={formData.term}
          onChange={handleSelectChange("term")}
          placeholder={getResourceValue("select_term", "Select Term")}
        />
      </div>
    </CustomFormModal>
  );
};

export default EditSectionModal;
